import { apiRequest } from './client.js';

export interface SurveyInstance {
  id: string;
  template_id: string;
  template_version: number;
  status: 'draft' | 'in_progress' | 'completed';
  responses: Record<string, unknown>;
  /** S15-C · Notas del auditor por questionRef. */
  auditor_notes?: Record<string, string>;
  score: unknown;
  completed_at: string | null;
  template?: { code: string; name: string };
}

export type AuditType = 'internal' | 'external' | 'certification';
export type CertPhase = 'stage_1' | 'stage_2' | 'surveillance' | 'recertification';

export interface AssessmentSummary {
  id: string;
  site_id: string;
  auditor_id: string;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  // S16-A · ISO 19011 §5.4
  audit_type: AuditType | null;
  audit_objectives: string[];
  audit_scope: string | null;
  audit_criteria: string[];
  cert_body: string | null;
  cert_phase: CertPhase | null;
  is_integrated: boolean;
}

export interface AssessmentDetail extends AssessmentSummary {
  signature?: { auditor_id: string; ts: string; hash: string } | null;
  instances: SurveyInstance[];
}

export interface CompleteResult {
  id: string;
  status: 'completed';
  completed_at: string;
  signature: { auditor_id: string; ts: string; hash: string };
  scores: { instanceId: string; weighted: number; perDomain: Record<string, number> }[];
}

export const assessmentsApi = {
  listOfSite: (siteId: string) =>
    apiRequest<{ assessments: AssessmentSummary[] }>(`/api/v1/sites/${siteId}/assessments`),
  create: (input: {
    site_id: string;
    surveys: { code: string; version?: number }[];
    notes?: string;
    // S16-A · ISO 19011 §5.4 metadata
    audit_type?: AuditType;
    audit_objectives?: string[];
    audit_scope?: string;
    audit_criteria?: string[];
    cert_body?: string;
    cert_phase?: CertPhase;
    is_integrated?: boolean;
  }) => apiRequest<AssessmentDetail>('/api/v1/assessments', { method: 'POST', body: input }),
  get: (id: string) => apiRequest<AssessmentDetail>(`/api/v1/assessments/${id}`),
  patchInstance: (
    assessmentId: string,
    instanceId: string,
    responses: Record<string, unknown>,
    auditorNotes?: Record<string, string>,
  ) =>
    apiRequest<{ instance: SurveyInstance }>(
      `/api/v1/assessments/${assessmentId}/instances/${instanceId}`,
      {
        method: 'PATCH',
        body: { responses, ...(auditorNotes !== undefined ? { auditor_notes: auditorNotes } : {}) },
      },
    ),
  complete: (id: string) =>
    apiRequest<CompleteResult>(`/api/v1/assessments/${id}/complete`, { method: 'POST' }),
};

export const photosApi = {
  upload: async (input: {
    assessmentId: string;
    instanceId: string;
    questionRef: string;
    file: File;
    capturedAt?: Date;
  }): Promise<{
    id: string;
    file_url: string;
    sha256: string;
    size_bytes: number;
    content_type: string;
  }> => {
    const fd = new FormData();
    fd.append('photo', input.file);
    fd.append('question_ref', input.questionRef);
    if (input.capturedAt) fd.append('captured_at', input.capturedAt.toISOString());
    const path = `/api/v1/assessments/${input.assessmentId}/instances/${input.instanceId}/photos`;
    // No usamos apiRequest porque lleva FormData (sin Content-Type explicito).
    // El token de Authorization se inyecta manualmente leyendo session storage.
    const session = sessionStorage.getItem('assessos.auth.session');
    const token = session ? (JSON.parse(session) as { token: string }).token : '';
    const init: RequestInit = { method: 'POST', body: fd };
    if (token) init.headers = { Authorization: `Bearer ${token}` };
    const res = await fetch(path, init);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `HTTP ${String(res.status)}`);
    }
    return (await res.json()) as {
      id: string;
      file_url: string;
      sha256: string;
      size_bytes: number;
      content_type: string;
    };
  },
};
